import { useEffect, useRef } from 'react';

/**
 * A11Y-04 (WCAG 2.4.2 Page Titled) — the browser tab and the history name the
 * page the person is on: "<page> · Fidelen", in the language the page is shown
 * in (the caller passes its translated heading, so a language switch retitles
 * the tab with it).
 *
 * The product name is the brand's, the same in both languages, as the
 * document's own default title (`index.html`) is.
 *
 * Pages can stack — a layout's heading under a page's — so the titles are kept
 * in mount order: the latest one names the tab, and when it goes the one under
 * it does again. With none left, the document's default comes back.
 */
export const APP_TITLE = 'Fidelen';

interface Entry {
  title: string;
}

const stack: Entry[] = [];

function apply(): void {
  const top = stack.findLast((entry) => entry.title !== '');

  document.title = top === undefined ? APP_TITLE : `${top.title} · ${APP_TITLE}`;
}

export function usePageTitle(title: string | null | undefined): void {
  // One place in the stack for as long as the page is shown: a new title (a
  // language switch, a record that loaded) changes it where it is.
  const own = useRef<Entry | null>(null);

  useEffect(() => {
    const entry: Entry = { title: '' };
    own.current = entry;
    stack.push(entry);

    return () => {
      const index = stack.indexOf(entry);

      if (index !== -1) stack.splice(index, 1);
      if (own.current === entry) own.current = null;
      apply();
    };
  }, []);

  useEffect(() => {
    if (own.current === null) return;

    own.current.title = title?.trim() ?? '';
    apply();
  }, [title]);
}
